/**
 * Local Storage Service for Documents and Transcripts
 * 
 * Stores documents locally in browser localStorage/IndexedDB
 * Allows users to manage which documents are in coach memory
 */

const DOCS_KEY = 'fidora_documents';
const TRANSCRIPTS_KEY = 'fidora_transcripts';

/**
 * Document structure:
 * {
 *   id: string,
 *   name: string,
 *   type: string (mime type),
 *   size: number,
 *   content: string (base64 or text),
 *   uploadedAt: timestamp,
 *   inCoachMemory: boolean (default true),
 *   lastModified: timestamp
 * }
 */

/**
 * Get all documents for current user
 */
export function getDocuments(userId) {
  try {
    const key = `${DOCS_KEY}_${userId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading documents:', error);
    return [];
  }
}

/**
 * Save document to local storage (with storage limit check)
 */
export async function saveDocument(userId, file, content, subscriptionTier = 'free') {
  try {
    // Safety: Maximum individual file size (5MB)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('File size exceeds the maximum limit of 5MB per file. Please upload a smaller file.');
    }

    // Safety: Block dangerous file types
    const blockedExtensions = [
      '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg', '.webp', '.ico', '.tiff', '.tif',
      '.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v', '.mpg', '.mpeg',
      '.js', '.jsx', '.ts', '.tsx', '.py', '.java', '.cpp', '.c', '.h', '.cs', '.php', '.rb', '.go', '.rs',
      '.html', '.htm', '.css', '.scss', '.sass', '.less', '.xml', '.json', '.yaml', '.yml',
      '.sh', '.bat', '.cmd', '.ps1', '.exe', '.dll', '.so', '.dylib', '.bin',
      '.zip', '.rar', '.7z', '.tar', '.gz', '.bz2'
    ];
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.odt'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (blockedExtensions.includes(fileExtension) && !allowedExtensions.includes(fileExtension)) {
      throw new Error('File type not allowed. Only PDF, DOC, DOCX, TXT, RTF, or ODT documents are permitted for safety.');
    }

    // Check storage limit
    const stats = getStorageStats(userId, subscriptionTier);
    const limitBytes = getStorageLimit(subscriptionTier);
    if ((stats.totalSize + file.size) > limitBytes) {
      const limitMB = limitBytes / (1024 * 1024);
      throw new Error(`Storage limit exceeded. Your ${subscriptionTier} plan allows ${limitMB}MB total. Please delete some files or upgrade your plan.`);
    }
    
    const documents = getDocuments(userId);
    
    const newDoc = {
      id: `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      type: file.type,
      size: file.size,
      content: content, // base64 or text content
      uploadedAt: new Date().toISOString(),
      inCoachMemory: true, // default: include in coach memory
      lastModified: new Date().toISOString()
    };
    
    documents.push(newDoc);
    
    const key = `${DOCS_KEY}_${userId}`;
    localStorage.setItem(key, JSON.stringify(documents));
    
    return newDoc;
  } catch (error) {
    console.error('Error saving document:', error);
    throw error;
  }
}

/**
 * Delete document
 */
export function deleteDocument(userId, docId) {
  try {
    const documents = getDocuments(userId);
    const filtered = documents.filter(doc => doc.id !== docId);
    
    const key = `${DOCS_KEY}_${userId}`;
    localStorage.setItem(key, JSON.stringify(filtered));
    
    return true;
  } catch (error) {
    console.error('Error deleting document:', error);
    return false;
  }
}

/**
 * Toggle document in/out of coach memory
 */
export function toggleDocumentMemory(userId, docId, inMemory) {
  try {
    const documents = getDocuments(userId);
    const doc = documents.find(d => d.id === docId);
    
    if (doc) {
      doc.inCoachMemory = inMemory;
      doc.lastModified = new Date().toISOString();
      
      const key = `${DOCS_KEY}_${userId}`;
      localStorage.setItem(key, JSON.stringify(documents));
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error toggling document memory:', error);
    return false;
  }
}

/**
 * Get documents that should be in coach memory
 */
export function getCoachMemoryDocuments(userId) {
  const documents = getDocuments(userId);
  return documents.filter(doc => doc.inCoachMemory);
}

/**
 * Read file as base64
 */
export async function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Read file as text
 */
export async function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

/**
 * Save chat transcript
 */
export function saveTranscript(userId, messages, title = null) {
  try {
    const transcripts = getTranscripts(userId);
    
    const transcript = {
      id: `transcript_${Date.now()}`,
      title: title || `Chat ${new Date().toLocaleDateString()}`,
      messages: messages,
      createdAt: new Date().toISOString(),
      inCoachMemory: true,
      messageCount: messages.length
    };
    
    transcripts.push(transcript);
    
    const key = `${TRANSCRIPTS_KEY}_${userId}`;
    localStorage.setItem(key, JSON.stringify(transcripts));
    
    return transcript;
  } catch (error) {
    console.error('Error saving transcript:', error);
    throw error;
  }
}

/**
 * Get all transcripts
 */
export function getTranscripts(userId) {
  try {
    const key = `${TRANSCRIPTS_KEY}_${userId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading transcripts:', error);
    return [];
  }
}

/**
 * Delete transcript
 */
export function deleteTranscript(userId, transcriptId) {
  try {
    const transcripts = getTranscripts(userId);
    const filtered = transcripts.filter(t => t.id !== transcriptId);
    
    const key = `${TRANSCRIPTS_KEY}_${userId}`;
    localStorage.setItem(key, JSON.stringify(filtered));
    
    return true;
  } catch (error) {
    console.error('Error deleting transcript:', error);
    return false;
  }
}

/**
 * Toggle transcript in/out of coach memory
 */
export function toggleTranscriptMemory(userId, transcriptId, inMemory) {
  try {
    const transcripts = getTranscripts(userId);
    const transcript = transcripts.find(t => t.id === transcriptId);
    
    if (transcript) {
      transcript.inCoachMemory = inMemory;
      
      const key = `${TRANSCRIPTS_KEY}_${userId}`;
      localStorage.setItem(key, JSON.stringify(transcripts));
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error toggling transcript memory:', error);
    return false;
  }
}

/**
 * Get transcripts in coach memory
 */
export function getCoachMemoryTranscripts(userId) {
  const transcripts = getTranscripts(userId);
  return transcripts.filter(t => t.inCoachMemory);
}

/**
 * Build coach context from documents and transcripts
 */
export function buildCoachContext(userId) {
  const docs = getCoachMemoryDocuments(userId);
  const transcripts = getCoachMemoryTranscripts(userId);
  
  let context = '';
  
  if (docs.length > 0) {
    context += '\n## USER DOCUMENTS\n';
    docs.forEach(doc => {
      context += `\n### ${doc.name}\n`;
      // For text files, include snippet of content
      if (doc.type.includes('text') || doc.type.includes('json')) {
        const snippet = doc.content.substring(0, 500);
        context += `${snippet}...\n`;
      } else {
        context += `[${doc.type} file, ${(doc.size / 1024).toFixed(1)} KB]\n`;
      }
    });
  }
  
  if (transcripts.length > 0) {
    context += '\n## PREVIOUS CONVERSATIONS\n';
    transcripts.slice(-3).forEach(t => {
      context += `\n### ${t.title}\n`;
      context += `(${t.messageCount} messages from ${new Date(t.createdAt).toLocaleDateString()})\n`;
    });
  }
  
  return context;
}

/**
 * Storage limits by subscription tier (in MB)
 */
export const STORAGE_LIMITS = {
  free: 5,      // 5MB limit for free tier (very small for safety)
  advisor: 10,  // 10MB limit for advisor tier
  coach: 15,    // 15MB limit for coach tier
  mentor: 20    // 20MB limit for mentor tier
};

/**
 * Get user's storage limit based on subscription tier
 */
export function getStorageLimit(subscriptionTier = 'free') {
  return (STORAGE_LIMITS[subscriptionTier] || STORAGE_LIMITS.free) * 1024 * 1024; // Convert to bytes
}

/**
 * Get storage stats with limit information
 */
export function getStorageStats(userId, subscriptionTier = 'free') {
  const docs = getDocuments(userId);
  const transcripts = getTranscripts(userId);
  
  const totalDocs = docs.length;
  const totalTranscripts = transcripts.length;
  const docsInMemory = docs.filter(d => d.inCoachMemory).length;
  const transcriptsInMemory = transcripts.filter(t => t.inCoachMemory).length;
  const totalSize = docs.reduce((sum, doc) => sum + doc.size, 0);
  const limitBytes = getStorageLimit(subscriptionTier);
  const limitMB = limitBytes / (1024 * 1024);
  const usedMB = totalSize / (1024 * 1024);
  const remainingMB = Math.max(0, limitMB - usedMB);
  const usagePercent = (totalSize / limitBytes * 100);
  const isAtLimit = totalSize >= limitBytes;
  const isNearLimit = usagePercent >= 80;
  
  return {
    totalDocs,
    totalTranscripts,
    docsInMemory,
    transcriptsInMemory,
    totalSize,
    totalSizeMB: usedMB.toFixed(2),
    limitMB: limitMB.toFixed(2),
    remainingMB: remainingMB.toFixed(2),
    usagePercent: usagePercent.toFixed(1),
    isAtLimit,
    isNearLimit,
    subscriptionTier
  };
}

/**
 * Check if adding a file would exceed storage limit
 */
export function wouldExceedLimit(userId, fileSize, subscriptionTier = 'free') {
  const stats = getStorageStats(userId, subscriptionTier);
  const limitBytes = getStorageLimit(subscriptionTier);
  return (stats.totalSize + fileSize) > limitBytes;
}

